"use client";

import { useRef } from "react";
import { getImageProps } from "next/image";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import type { GROQImage } from "@/sanity/lib/definitions";
import type { PageTradingCompetitionSchema } from "@/sanity/pages/PageTradingCompetitionTemplate";
import CtaHover from "./CtaHover";
import SelectionHighlight, { type SelectionGeometry } from "./SelectionHighlight";
import TradingCompetitionCountdown, { unitLabelsFor } from "./TradingCompetitionCountdown";
import { homeHref } from "./TradingCompetitionNav";
import { linkHref, useCtaHref } from "./ctaHref";
import { at, useReveal } from "./motion";

/** "yet?" selection: Figma 64×37 at 34px (mobile), 162×82 at 72px (desktop), measured against the glyphs. */
const TITLE_SELECTION = {
  mobile: {
    font: 34,
    box: [-5.48, -2.24, 64, 37],
    word: 53.28,
    handles: [[-0.75, 0, 1.5, 33], [-2, 33, 4, 4], [62, 0, 4, 4], [63.25, 4, 1.5, 33]],
  },
  desktop: {
    font: 72,
    box: [-22.78, -5.87, 162, 82],
    word: 114.31,
    handles: [[0, 0, 2, 82], [-2, 76, 6, 6], [158, 0, 6, 6], [160, 0, 2, 82]],
  },
} satisfies Record<string, SelectionGeometry>;

/** Figma's CROP imageTransforms as fractions of the photo: x, y, width, height. */
const HALL_CROP = {
  mobile: [0.4042986, 0.0532468, 0.1912662, 0.5971807],
  desktop: [0.2785266, 0.3121203, 0.4429468, 0.4429468],
};

/** The photo cropped by Sanity to the visible box, so that box loads at full resolution. */
function hallSrc(image: GROQImage, [x, y, w, h]: number[]) {
  const rect = [x * image.width, y * image.height, w * image.width, h * image.height].map(Math.round).join(",");
  return `${image.src}?rect=${rect}`;
}

/** One <picture>, so each layout downloads only its own crop. */
function HallPhoto({ image }: { image: GROQImage }) {
  const common = { alt: "", fill: true, sizes: "100vw", quality: 90 };
  const { props: { srcSet: desktop } } = getImageProps({ ...common, src: hallSrc(image, HALL_CROP.desktop) });
  const { props: { srcSet: mobile, ...rest } } = getImageProps({ ...common, src: hallSrc(image, HALL_CROP.mobile) });
  return (
    <picture>
      <source media="(min-width: 993px)" srcSet={desktop} sizes="100vw" />
      {/* eslint-disable-next-line @next/next/no-img-element, jsx-a11y/alt-text */}
      <img {...rest} srcSet={mobile} className="object-fill" />
    </picture>
  );
}

/**
 * Final call to action over the trophy hall, then the page footer. On desktop the photo runs
 * under both (Figma frame 1920×1049); on mobile it fills the 708px CTA block and the footer
 * sits on solid green. Each layout loads its own Figma crop, stretched to the box as Figma does.
 */
export default function TradingCompetitionFinalCta({ data }: { data: PageTradingCompetitionSchema }) {
  const language = data.settings?.language;
  const primaryHref = useCtaHref(data.ctaPrimary, language);
  const secondaryHref = useCtaHref(data.ctaSecondary, language);

  // Entrance: copy rises, "yet?" gets selected and its right bar keeps blinking like a caret,
  // the countdown rises. Scroll: the hall settles from a slight zoom to Figma's crop exactly when
  // the page bottoms out. Loop: the light shaft breathes; "Get funded" catches a sheen.
  const [ctaRef, ctaReveal] = useReveal({ threshold: 0.25 });
  const reduceMotion = useReducedMotion();
  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start end", "end end"] });
  const hallScale = useTransform(scrollYProgress, [0, 1], [1.08, 1]);

  return (
    <section ref={sectionRef} id="get-funded" className="relative isolate overflow-hidden bg-[#001b14]">
      {/* ── Background ─────────────────────────────────────────────────── */}
      <div aria-hidden="true" className="absolute inset-x-0 top-0 -z-10 h-[88.5rem] overflow-hidden lg:inset-0 lg:h-auto">
        {data.ctaBackground && (
          <motion.div className="absolute inset-0 origin-[50%_40%]" style={reduceMotion ? undefined : { scale: hallScale }}>
            <HallPhoto image={data.ctaBackground} />
          </motion.div>
        )}
        {/* the light shaft over the trophy breathes */}
        <i className="tc-beam absolute top-[14rem] left-1/2 h-[56rem] w-[36rem] -translate-x-1/2 bg-[radial-gradient(50%_50%_at_50%_50%,rgba(200,255,232,0.16),rgba(200,255,232,0)_70%)] mix-blend-screen lg:top-[-6rem] lg:h-[90rem] lg:w-[60rem]" />
        {/* mobile: Figma flips its footer fade to darken the top, then lays the reading gradient over it */}
        <i className="absolute inset-x-0 top-[-5.844rem] bottom-[5.969rem] bg-[linear-gradient(180deg,#001b14_0%,#001b14_45%,rgba(0,27,20,0)_100%)] lg:hidden" />
        <i className="absolute inset-x-0 top-[0.031rem] bottom-[-3.406rem] bg-[linear-gradient(180deg,rgba(0,20,15,0.92)_0%,rgba(0,20,15,0.6)_34%,rgba(0,20,15,0)_55%,rgba(0,20,15,0)_85%,rgba(0,20,15,0.7)_100%)] lg:hidden" />
        {/* desktop: reading gradient over the 960px CTA, then the fade into the footer */}
        <i className="absolute inset-x-0 top-0 hidden h-[120rem] bg-[linear-gradient(180deg,rgba(0,20,15,0.92)_0%,rgba(0,20,15,0.6)_34%,rgba(0,20,15,0)_55%,rgba(0,20,15,0)_85%,rgba(0,20,15,0.7)_100%)] lg:block" />
        <i className="absolute inset-x-0 top-[100rem] hidden h-[31.125rem] bg-[linear-gradient(180deg,rgba(0,27,20,0)_0%,#001b14_55%,#001b14_100%)] lg:block" />
      </div>

      {/* ── CTA ────────────────────────────────────────────────────────── */}
      <div
        ref={ctaRef}
        data-reveal={ctaReveal}
        className="relative flex h-[88.5rem] flex-col items-center gap-[2.5rem] overflow-hidden px-[2rem] pt-[7rem] lg:h-[120rem] lg:gap-[2rem] lg:overflow-visible lg:px-0 lg:pt-[12rem]">
        {data.ctaEyebrow && (
          <p style={at(0)} className="tc-rv-rise mb-0 font-blacker text-[2.25rem] leading-[0.9444] font-normal whitespace-nowrap text-white/[0.72] italic lg:text-[2.5rem]">
            {data.ctaEyebrow}
          </p>
        )}

        <h2 style={at(0.12, 1)} className="tc-rv-rise mb-0 text-center font-geist text-[4.25rem] leading-[1.04] font-medium tracking-[-0.1913rem] whitespace-pre text-white lg:text-[9rem] lg:tracking-[-0.36rem]">
          {data.ctaTitleBefore}
          {/* Figma's own spacing before the highlight: two spaces on mobile, three on desktop */}
          <span className="lg:hidden">{"  "}</span>
          <span className="hidden lg:inline">{"   "}</span>
          <span className="relative font-blacker leading-[0] font-normal text-[#bae9d6] italic lg:text-[#d6eee7]">
            <SelectionHighlight
              {...TITLE_SELECTION}
              fill="rgba(218,255,245,0.12)"
              mobileFill="rgba(186,233,214,0.1)"
              handle="#bae9d6"
              delay={0.85}
              caret
            />
            <span className="relative font-blacker">{data.ctaTitleHighlight}</span>
          </span>
        </h2>

        {data.ctaDescription && (
          // mobile: Figma squeezes the two lines into a 37px box, centred
          <div style={at(0.3)} className="tc-rv-rise flex h-[4.625rem] w-full items-center justify-center lg:h-auto lg:w-[65rem]">
            <p className="mb-0 text-center font-geist text-[2rem] leading-[1.5] font-normal text-white/[0.78] lg:text-[2.25rem]">
              {data.ctaDescription}
            </p>
          </div>
        )}

        <TradingCompetitionCountdown
          variant="glass"
          label={data.countdownLabel}
          target={data.countdownTarget}
          unitLabels={unitLabelsFor(language)}
          className="relative shrink-0 text-[0.11006rem] lg:absolute lg:top-[93.875rem] lg:left-1/2 lg:-translate-x-1/2"
          revealDelay={0.5}
        />

        <div style={at(0.42)} className="tc-rv-rise flex w-full flex-col gap-[1.25rem] lg:w-auto lg:flex-row lg:gap-[3rem] lg:pt-[1.5rem]">
          {data.ctaPrimary?.text && (
            <a
              href={primaryHref}
              className="tc-sheen group relative flex h-[6rem] items-center justify-center overflow-hidden rounded-[0.25rem] px-[3rem] lg:h-[6.5rem]">
              <CtaHover
                label={data.ctaPrimary.text}
                fills={["bg-white", "bg-[#3fa288]"]}
                labelClassName="font-geist text-[2rem] leading-[normal] font-semibold tracking-[-0.04rem] whitespace-nowrap text-black"
                hoverLabelClassName="text-white"
              />
            </a>
          )}
          {data.ctaSecondary?.text && (
            <a
              href={secondaryHref}
              className="group relative flex h-[6rem] items-center justify-center overflow-hidden rounded-[0.25rem] border border-solid border-white/10 bg-white/10 p-[1rem] font-geist text-[2rem] leading-[normal] font-medium whitespace-nowrap text-white lg:h-auto lg:w-[20.875rem]">
              <CtaHover label={data.ctaSecondary.text} />
            </a>
          )}
        </div>
      </div>

      {/* ── Footer ─────────────────────────────────────────────────────── */}
      <footer className="relative flex flex-col items-start gap-[2rem] border-t border-solid border-white/10 bg-[#001b14] px-[2rem] py-[3.5rem] lg:h-[11.125rem] lg:flex-row lg:items-center lg:justify-between lg:bg-transparent lg:px-[45rem] lg:py-[2.5rem]">
        <div className="flex flex-col items-start gap-[2rem] lg:flex-row lg:items-center lg:gap-[4rem]">
          <a href={homeHref(language)} aria-label="IQ Capital home" className="shrink-0">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/trading-competition/logo-white.svg"
              alt="IQ Capital"
              className="h-[2.6744rem] w-[14.44rem] lg:h-[3.1464rem] lg:w-[16.988rem]"
            />
          </a>
          {data.footerCopyright && (
            <p className="mb-0 font-geist text-[1.625rem] leading-[1.5] font-normal text-white/50 lg:w-[76rem] lg:text-[1.75rem]">
              {data.footerCopyright}
            </p>
          )}
        </div>
        {!!data.footerLinks?.length && (
          <nav aria-label="Legal" className="flex items-center gap-[2.5rem] lg:gap-[3rem]">
            {data.footerLinks.map((link, i) => (
              <a
                key={link._key ?? i}
                href={linkHref(link, language)}
                className="font-geist text-[1.75rem] leading-[normal] font-normal whitespace-nowrap text-white/[0.72]">
                {link.text}
              </a>
            ))}
          </nav>
        )}
      </footer>
    </section>
  );
}
